import { useState, useMemo } from 'react'
import { FaSearch } from 'react-icons/fa'
import RealisticPlate from '@/components/shop/RealisticPlate'
import {
    type PlateSize,
    type PlateType,
    type VehicleCategory,
    type PlateInput,
    getPlateSizesForCategory,
    getPlateTypesForCategory,
    getRulesForDimensions
} from '@/services/rules/plateRulesEngine'

export interface SearchData {
    cityCode: string
    letters: string
    numbers: string
    vehicleCategory: VehicleCategory
    plateSize: PlateSize
    plateType: PlateType
    seasonFrom?: number
    seasonTo?: number
}

interface PlateSearchConfiguratorProps {
    onSearch: (data: SearchData) => void
    isSearching?: boolean
    /** 'search' = API search (default), 'direct' = add to cart directly without API */
    mode?: 'search' | 'direct'
    /** Called whenever the city code input changes */
    onCityCodeChange?: (cityCode: string) => void
    /** Called when the user moves focus away from the city code zone */
    onCityCodeExit?: (cityCode: string) => void
}

export default function PlateSearchConfigurator({ onSearch, isSearching, mode = 'search', onCityCodeChange, onCityCodeExit }: PlateSearchConfiguratorProps) {
    const [vehicle, setVehicle] = useState<VehicleCategory>('PKW')

    const availableSizes = getPlateSizesForCategory(vehicle)
    const availableTypes = getPlateTypesForCategory(vehicle)

    const defaultSize = availableSizes.find(s => s.width === 520 && s.layout === 'einzeilig') || availableSizes[0]

    const [size, setSize] = useState<PlateSize>(defaultSize)
    const [type, setType] = useState<PlateType>(availableTypes[0])

    const [input, setInput] = useState<PlateInput>({ cityCode: '', letters: '', numbers: '' })

    const handleInputChange = (newInput: PlateInput) => {
        setInput(newInput)
        if (newInput.cityCode !== input.cityCode) {
            onCityCodeChange?.(newInput.cityCode)
        }
    }
    const [seasonFrom, setSeasonFrom] = useState(4)
    const [seasonTo, setSeasonTo] = useState(10)

    const isSeasonal = type.includes('SEASON')

    const activeRule = useMemo(() => {
        const rules = getRulesForDimensions(size.width, size.height, type.includes('SEASON'), type === 'E' || type === 'E_SEASON', type === 'H' || type === 'H_SEASON')
        if (rules.length > 0) return rules[rules.length - 1]
        // Fallback to REGULAR rule for this size when no specific type rule exists
        const fallback = getRulesForDimensions(size.width, size.height, false, false, false)
        return fallback[fallback.length - 1] || fallback[0]
    }, [size, type])

    const handleVehicleChange = (newCat: VehicleCategory) => {
        setVehicle(newCat)
        const newSizes = getPlateSizesForCategory(newCat)
        const newTypes = getPlateTypesForCategory(newCat)
        setSize(newSizes[0])
        setType(newTypes[0])
    }

    const canSearch = input.cityCode.length >= 1 && ((input.letters?.length || 0) >= 1 || (input.numbers?.length || 0) >= 1)

    const handleSearch = () => {
        if (!canSearch || isSearching) return
        onSearch({
            cityCode: input.cityCode,
            letters: input.letters || '',
            numbers: input.numbers || '',
            vehicleCategory: vehicle,
            plateSize: size,
            plateType: type,
            ...(isSeasonal ? { seasonFrom, seasonTo } : {}),
        })
    }

    return (
        <div className="w-full bg-dark-900/60 backdrop-blur-xl border border-dark-700/50 rounded-2xl p-6 sm:p-8 shadow-2xl">
            {/* Configuration Dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Fahrzeug *</label>
                    <div className="relative">
                        <select
                            value={vehicle}
                            onChange={(e) => handleVehicleChange(e.target.value as VehicleCategory)}
                            className="w-full bg-dark-800/80 border border-dark-600 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none transition-all"
                        >
                            <option value="PKW">Autokennzeichen</option>
                            <option value="MOTORCYCLE">Motorrad</option>
                            <option value="TRAILER">Anhänger</option>
                            <option value="LEICHTKRAFT">Leichtkraftrad</option>
                            <option value="TRAKTOR">Traktor</option>
                            <option value="TRUCK">LKW</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                        </div>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Größe *</label>
                    <div className="relative">
                        <select
                            value={JSON.stringify(size)}
                            onChange={(e) => setSize(JSON.parse(e.target.value) as PlateSize)}
                            className="w-full bg-dark-800/80 border border-dark-600 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none transition-all"
                        >
                            {availableSizes.map(s => (
                                <option key={`${s.width}x${s.height}-${s.layout}`} value={JSON.stringify(s)}>
                                    {`${s.width} x ${s.height} mm (${s.layout === 'zweizeilig' ? 'Zweizeilig' : 'Einzeilig'})`}
                                </option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                        </div>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Typ *</label>
                    <div className="relative">
                        <select
                            value={type}
                            onChange={(e) => setType(e.target.value as PlateType)}
                            className="w-full bg-dark-800/80 border border-dark-600 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none transition-all"
                        >
                            {availableTypes.map(t => (
                                <option key={t} value={t}>
                                    {t === 'REGULAR' ? 'Standardkennzeichen' :
                                        t === 'REGULAR_SEASON' ? 'Saisonkennzeichen' :
                                            t === 'E' ? 'E-Kennzeichen' :
                                                t === 'H' ? 'H-Kennzeichen' :
                                                    t === 'GREEN' ? 'Grünes Kennzeichen' :
                                                        t === 'E_SEASON' ? 'E-Saisonkennzeichen' :
                                                            t === 'H_SEASON' ? 'H-Saisonkennzeichen' : t}
                                </option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Season Month Selectors - shown when seasonal type is selected */}
            {isSeasonal && (
                <div className="grid grid-cols-2 gap-4 mb-8">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Saison von *</label>
                        <div className="relative">
                            <select
                                value={seasonFrom}
                                onChange={(e) => setSeasonFrom(Number(e.target.value))}
                                className="w-full bg-dark-800/80 border border-dark-600 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none transition-all"
                            >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                    <option key={m} value={m}>
                                        {new Date(2000, m - 1).toLocaleString('de-DE', { month: 'long' })} ({String(m).padStart(2, '0')})
                                    </option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Saison bis *</label>
                        <div className="relative">
                            <select
                                value={seasonTo}
                                onChange={(e) => setSeasonTo(Number(e.target.value))}
                                className="w-full bg-dark-800/80 border border-dark-600 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none transition-all"
                            >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                    <option key={m} value={m}>
                                        {new Date(2000, m - 1).toLocaleString('de-DE', { month: 'long' })} ({String(m).padStart(2, '0')})
                                    </option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="mb-2">
                <h3 className="text-lg font-semibold text-white mb-4">Ihre Wunschkombination</h3>
            </div>

            {/* Interactive Plate Search Area */}
            <div className="flex flex-col items-center gap-5 bg-dark-800/40 p-5 sm:p-6 rounded-2xl border border-dark-700/50">

                {/* The Realistic Plate Graphic - Full Width */}
                <div className="w-full flex items-center justify-center">
                    <RealisticPlate
                        plateSize={size}
                        plateType={type}
                        vehicleCategory={vehicle}
                        input={input}
                        interactive={true}
                        rule={activeRule}
                        onInputChange={handleInputChange}
                        onCityCodeExit={onCityCodeExit}
                        seasonFrom={seasonFrom}
                        seasonTo={seasonTo}
                    />
                </div>

                {/* Info & Search Button - Below Plate */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 w-full">
                    <div className="flex items-start gap-3 text-gray-400 bg-dark-900/50 p-3 rounded-xl">
                        <div className="bg-dark-700/50 p-1.5 rounded-full text-primary-400 mt-0.5 flex-shrink-0">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <p className="text-sm">Tipp: Verwenden Sie ein <strong className="text-white">?</strong> als Platzhalter für einzelne Zeichen.</p>
                    </div>

                    <button
                        onClick={handleSearch}
                        disabled={!canSearch || isSearching}
                        className={`w-full sm:w-auto px-8 py-4 font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 transition-all whitespace-nowrap flex-shrink-0 ${canSearch && !isSearching
                            ? mode === 'direct'
                                ? 'bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white shadow-green-500/30 cursor-pointer hover:scale-[1.02] active:scale-[0.98]'
                                : 'bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 text-white shadow-primary-500/30 cursor-pointer hover:scale-[1.02] active:scale-[0.98]'
                            : 'bg-dark-700 text-gray-500 cursor-not-allowed shadow-none'
                            }`}
                    >
                        {isSearching ? (
                            <>
                                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block"></span>
                                <span>SUCHE LÄUFT...</span>
                            </>
                        ) : mode === 'direct' ? (
                            <span>IN DEN WARENKORB</span>
                        ) : (
                            <>
                                <FaSearch />
                                <span>VERFÜGBARKEIT PRÜFEN</span>
                            </>
                        )}
                    </button>
                </div>

            </div>
        </div>
    )
}
